// ─── Health metrics & alerts ──────────────────────────────────────────────────
import { mkKey, currSym, getDaysInMonth, parseDate } from "./utils.js";
import { getYearRange, getOverdueExpenses, detectAppIssues } from "./marketplaces.js";
import { generateOrderExpenses, generateRecurringItems } from "./expenses.js";

// ─── Business Health Score (0-100) ──────────────────────────────────────────
// Penalty buckets (max -100 if everything is bad):
//   Runway          → up to -30
//   Stockout days   → up to -25
//   Breakeven cov.  → up to -25
//   Today balance   → up to -20
//   Cash-to-Cash    → up to -25  (NEW — was missing, so a 2400-day cycle still
//                                  scored 100% — health is meaningless without it)
//
// Cash-to-Cash is the time between paying for inventory and getting the cash
// back from the customer. The longer it is, the more working capital the
// business needs to keep operating. 60-90 days is typical for a healthy
// e-commerce business; >180 days means the business is bleeding cash.
export function computeHealthScore({ runway, stockoutDaysAway, breakeven, todayBalance, bufferThreshold = 0, curTarget = 0, cashToCashDays = null }) {
  let score = 100;

  if (runway === Infinity || runway === null) score -= 0;
  else if (runway > 90) score -= 0;
  else if (runway > 60) score -= 5;
  else if (runway > 30) score -= 15;
  else if (runway > 14) score -= 22;
  else if (runway > 7) score -= 27;
  else score -= 30;

  if (stockoutDaysAway === null || stockoutDaysAway === undefined) score -= 0;
  else if (stockoutDaysAway > 90) score -= 0;
  else if (stockoutDaysAway > 60) score -= 5;
  else if (stockoutDaysAway > 30) score -= 12;
  else if (stockoutDaysAway > 14) score -= 18;
  else score -= 25;

  if (breakeven && breakeven.minUnits !== Infinity && curTarget > 0) {
    const coverage = curTarget / breakeven.minUnits;
    if (coverage >= 1.5) score -= 0;
    else if (coverage >= 1.2) score -= 5;
    else if (coverage >= 1.0) score -= 10;
    else if (coverage >= 0.8) score -= 18;
    else score -= 25;
  }

  const effectiveBuffer = bufferThreshold || 0;
  if (todayBalance > effectiveBuffer * 3) score -= 0;
  else if (todayBalance > effectiveBuffer * 2) score -= 3;
  else if (todayBalance > effectiveBuffer) score -= 8;
  else if (todayBalance > 0) score -= 14;
  else score -= 20;

  // Cash-to-Cash penalty — longer cycles drain working capital faster.
  // Scale: <=60d healthy, 60-90d normal, 90-120d slow, 120-180d concerning,
  // 180-365d alarming, >365d broken (bleeding cash). Null/0 = no penalty
  // (no data to evaluate, equivalent to "stockout days = null").
  if (cashToCashDays != null && cashToCashDays > 0) {
    if (cashToCashDays <= 60) score -= 0;
    else if (cashToCashDays <= 90) score -= 5;
    else if (cashToCashDays <= 120) score -= 10;
    else if (cashToCashDays <= 180) score -= 15;
    else if (cashToCashDays <= 365) score -= 20;
    else score -= 25;  // >365 days — broken cycle
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

// ─── Comprehensive alert generation ──────────────────────────────────────────
export function computeAlerts({
  dailyData, availableToday, months, orders, recurringExpenses,
  decisions, modifiers, marketplaces, visibleYears, today, alertThresholds,
  // inventorySummary is the resolved-stock object from computeInventorySummary
  // (added for the stock-hierarchy work). When passed, the low-stock alerts
  // read .totalUnits from here instead of guessing from months[].unitsOverride.
  inventorySummary = null,
}) {
  try {
  const alerts = [];
  if (!today || !(today instanceof Date)) return alerts;
  const todayMs = today.getTime();
  const critDays = alertThresholds?.critical ?? 7;
  const warnDays = alertThresholds?.warning ?? 30;
  const infoDays = alertThresholds?.info ?? 90;
  const bufferThreshold = Number(alertThresholds?.bufferThreshold) || 0;
  const bufferLabel = bufferThreshold > 0 ? `${currSym()}${bufferThreshold.toLocaleString()} buffer` : `${currSym()}0`;

  const future = (dailyData || []).filter(d => {
    const dMs = new Date(d.year, d.month - 1, d.day).getTime();
    return dMs >= todayMs;
  });

  const negCrit = future.find(d => {
    const dMs = new Date(d.year, d.month - 1, d.day).getTime();
    return d.balance < bufferThreshold && dMs <= todayMs + critDays * 86_400_000;
  });
  const negWarn = !negCrit && future.find(d => {
    const dMs = new Date(d.year, d.month - 1, d.day).getTime();
    return d.balance < bufferThreshold && dMs <= todayMs + warnDays * 86_400_000;
  });
  const negInfo = !negCrit && !negWarn && future.find(d => {
    const dMs = new Date(d.year, d.month - 1, d.day).getTime();
    return d.balance < bufferThreshold && dMs <= todayMs + infoDays * 86_400_000;
  });

  function buildCashGapDetail(breachDay) {
    if (!breachDay) return "";
    const key = mkKey(breachDay.year, breachDay.month);
    const d = new Date(breachDay.year, breachDay.month - 1, breachDay.day);
    const dateStr = d.toLocaleDateString("en-GB",{day:"2-digit",month:"short"});
    const parts = [`Projected to breach ${bufferLabel} around ${dateStr}.`];

    const oExps = generateOrderExpenses(orders || []);
    const monthOrderExps = (oExps[key] || []).filter(e => {
      const mData = months[key] || {};
      return !(mData.orderExpPaid||{})[e.id];
    });
    if (monthOrderExps.length > 0) {
      const topOrders = monthOrderExps.sort((a,b)=>b.amount-a.amount).slice(0,3);
      parts.push("Order payments: " + topOrders.map(e => `${e.what.split("—")[0].trim()} ${currSym()}${Math.round(e.amount).toLocaleString()}`).join(", ") + ".");
    }

    const recItems = (recurringExpenses || []).filter(r => !r.isIncome);
    if (recItems.length > 0) {
      const recTotal = recItems.reduce((s,r) => s + (r.amount||0), 0);
      if (recTotal > 0) parts.push(`Recurring expenses: ${currSym()}${Math.round(recTotal).toLocaleString()}/mo (${recItems.length} items).`);
    }

    const mData = months[key] || {};
    const manualUnpaid = (mData.expenses || []).filter(e => !e.paid);
    if (manualUnpaid.length > 0) {
      const manTotal = manualUnpaid.reduce((s,e) => s + (e.amount||0), 0);
      parts.push(`Manual expenses: ${currSym()}${Math.round(manTotal).toLocaleString()} (${manualUnpaid.length} items).`);
    }

    parts.push(`Projected income this month: ${currSym()}${Math.round(breachDay.income * 30).toLocaleString()}.`);
    return parts.join(" ");
  }

  const negBreach = negCrit || negWarn || negInfo;
  const gapDetail = buildCashGapDetail(negBreach);

  if (negCrit) {
    alerts.push({ id:"neg7", severity:"critical", icon:"🚨", title:`Balance drops below ${bufferLabel} in ${critDays} days`, detail:gapDetail, page:"dashboard", action:"View chart" });
  } else if (negWarn) {
    alerts.push({ id:"neg30", severity:"warning", icon:"⚠️", title:`Balance drops below ${bufferLabel} within ${warnDays} days`, detail:gapDetail, page:"dashboard", action:"View chart" });
  } else if (negInfo) {
    alerts.push({ id:"neg90", severity:"info", icon:"📉", title:`Balance drops below ${bufferLabel} within ${infoDays} days`, detail:gapDetail, page:"dashboard", action:"View chart" });
  }

  let overdueCount = 0;
  let overdueTotal = 0;

  const orderExps = generateOrderExpenses(orders || []);
  for (const [, exps] of Object.entries(orderExps)) {
    for (const exp of exps) {
      const d = parseDate(exp.date);
      if (d && d < today) {
        const mKey = mkKey(d.getFullYear(), d.getMonth() + 1);
        if (!(months[mKey]?.orderExpPaid || {})[exp.id]) {
          overdueCount++;
          overdueTotal += exp.amount || 0;
        }
      }
    }
  }

  const recItems = generateRecurringItems(
    (recurringExpenses || []).filter(r => !r.isIncome),
    visibleYears || [today.getFullYear()]
  );
  for (const [, items] of Object.entries(recItems)) {
    for (const item of items) {
      const d = parseDate(item.date);
      if (d && d < today) {
        const mKey = mkKey(d.getFullYear(), d.getMonth() + 1);
        const ov = (months[mKey]?.recurringOverrides || {})[item.id||item.recurringId] || {};
        if (!ov.paid && !ov.deleted) {
          overdueCount++;
          overdueTotal += item.amount || 0;
        }
      }
    }
  }

  if (overdueCount > 0) {
    alerts.push({
      id:"overdue", severity:"critical", icon:"🔴",
      title:`${overdueCount} overdue payment${overdueCount > 1 ? "s" : ""} — ${currSym()}${Math.round(overdueTotal).toLocaleString()} total`,
      detail:"These payments are past their due date and unpaid. Mark them paid or delay them in Cash Calendar.",
      page:"cashflow", action:"Open Cash Calendar",
    });
  }

  const cutoff7 = new Date(todayMs + 7 * 86_400_000);
  let soon7Count = 0;
  let soon7Total = 0;

  for (const [, exps] of Object.entries(orderExps)) {
    for (const exp of exps) {
      const d = parseDate(exp.date);
      if (d && d >= today && d <= cutoff7) {
        const mKey = mkKey(d.getFullYear(), d.getMonth() + 1);
        if (!(months[mKey]?.orderExpPaid || {})[exp.id]) {
          soon7Count++; soon7Total += exp.amount || 0;
        }
      }
    }
  }
  for (const [, items] of Object.entries(recItems)) {
    for (const item of items) {
      const d = parseDate(item.date);
      if (d && d >= today && d <= cutoff7) {
        const mKey = mkKey(d.getFullYear(), d.getMonth() + 1);
        const ov = (months[mKey]?.recurringOverrides || {})[item.id||item.recurringId] || {};
        if (!ov.paid && !ov.deleted) { soon7Count++; soon7Total += item.amount || 0; }
      }
    }
  }

  if (soon7Count > 0) {
    alerts.push({
      id:"soon7", severity:"warning", icon:"📅",
      title:`${soon7Count} payment${soon7Count > 1 ? "s" : ""} due in 7 days — ${currSym()}${Math.round(soon7Total).toLocaleString()}`,
      detail:"Review upcoming payments and make sure funds are ready.",
      page:"cashflow", action:"Open Cash Calendar",
    });
  }

  const todayKey = mkKey(today.getFullYear(), today.getMonth() + 1);
  const todayTarget = months[todayKey]?.target || 0;
  const todayUnitsSold = months[todayKey]?.unitsSoldThisMonth || 0;
  // Prefer the resolved stock from the hierarchy (inventorySummary.totalUnits)
  // when it's available — that respects pool/marketplace/product overrides
  // and the API/manual cascade. Fall back to the legacy units-sold proxy
  // only when no inventorySummary was passed (older callers).
  const unitsLeft = inventorySummary && Number.isFinite(Number(inventorySummary.totalUnits))
    ? Math.max(0, Number(inventorySummary.totalUnits))
    : Math.max(0, (months[todayKey]?.unitsOverride ?? todayUnitsSold));

  if (todayTarget > 0 && unitsLeft > 0) {
    let earliestInStock = null;
    for (const order of (orders || [])) {
      for (const batch of (order.containerBatches || [])) {
        if (batch.inStockDate) {
          const d = parseDate(batch.inStockDate);
          if (d && d > today) {
            if (!earliestInStock || d < earliestInStock) earliestInStock = d;
          }
        }
      }
    }

    if (earliestInStock) {
      const daysUntilStock = Math.ceil((earliestInStock - todayMs) / 86_400_000);
      const dailySales = todayTarget / 30;
      const daysOfStock = dailySales > 0 ? Math.floor(unitsLeft / dailySales) : Infinity;

      if (daysOfStock < daysUntilStock) {
        alerts.push({
          id:"stockout", severity:"critical", icon:"📦",
          title:`Stock-out risk — you run out ~${daysOfStock} days before next restock`,
          detail:`At current sales rate you have ~${daysOfStock} days of stock but next arrival is in ${daysUntilStock} days. Consider a reorder or expedited shipping.`,
          page:"orders", action:"Open Supplier Orders",
        });
      } else if (daysOfStock < daysUntilStock + 14) {
        alerts.push({
          id:"stocklow", severity:"warning", icon:"📦",
          title:`Stock running low — only ${daysOfStock - daysUntilStock} days buffer before next restock`,
          detail:"You'll have a thin safety margin when your next order arrives. Consider ordering sooner.",
          page:"orders", action:"Open Supplier Orders",
        });
      }
    } else if (unitsLeft < todayTarget * 0.5) {
      alerts.push({
        id:"noorder", severity:"warning", icon:"📦",
        title:`Units low (${unitsLeft.toLocaleString()} left) with no supplier order scheduled`,
        detail:"You have no active supplier orders. If you need to reorder, plan soon to avoid a stock-out.",
        page:"orders", action:"Open Supplier Orders",
      });
    }
  }

  const cutoff30 = new Date(todayMs + 30 * 86_400_000);
  const upcomingDecs = (decisions || []).filter(d => {
    if (d.status === "done" || d.status === "cancelled") return false;
    const dt = d.date;
    if (!dt) return false;
    let date = null;
    if (dt.prec === "day"   && dt.day)     date = parseDate(dt.day);
    if (dt.prec === "month" && dt.month)   { const [y,m] = dt.month.split("-").map(Number); date = new Date(y, m-1, 1); }
    if (dt.prec === "quarter" && dt.quarter) { const [y,q] = dt.quarter.split("-Q"); date = new Date(parseInt(y), (parseInt(q)-1)*3, 1); }
    return date && date >= today && date <= cutoff30;
  });

  if (upcomingDecs.length > 0) {
    alerts.push({
      id:"decisions", severity:"info", icon:"📌",
      title:`${upcomingDecs.length} planned decision${upcomingDecs.length > 1 ? "s" : ""} coming up this month`,
      detail:upcomingDecs.slice(0,3).map(d => (d.icon || "📌") + " " + d.name).join(" · "),
      page:"planner", action:"Open Planner",
    });
  }

  const overdueDecs = (decisions || []).filter(d => {
    if (d.status === "done" || d.status === "cancelled") return false;
    const dt = d.date;
    if (!dt) return false;
    let date = null;
    if (dt.prec === "day" && dt.day) date = parseDate(dt.day);
    if (dt.prec === "month" && dt.month) { const [y,m] = dt.month.split("-").map(Number); date = new Date(y, m, 0); }
    if (dt.prec === "quarter" && dt.quarter) { const [y,q] = dt.quarter.split("-Q"); date = new Date(parseInt(y), parseInt(q)*3, 0); }
    return date && date < today;
  });

  if (overdueDecs.length > 0) {
    const mustOverdue = overdueDecs.filter(d => (d.priority || "should") === "must");
    const shouldOverdue = overdueDecs.filter(d => (d.priority || "should") === "should");
    const niceOverdue = overdueDecs.filter(d => (d.priority || "should") === "nice");
    if (mustOverdue.length > 0) {
      alerts.push({ id:"overdue_must", severity:"critical", icon:"🚨",
        title:`${mustOverdue.length} MUST-DO decision${mustOverdue.length>1?"s":""} overdue`,
        detail:mustOverdue.map(d=>(d.icon||"📌")+" "+d.name).join(" · "), page:"planner", action:"Open Planner" });
    }
    if (shouldOverdue.length > 0) {
      alerts.push({ id:"overdue_should", severity:"warning", icon:"⏰",
        title:`${shouldOverdue.length} overdue decision${shouldOverdue.length>1?"s":""} need attention`,
        detail:shouldOverdue.map(d=>(d.icon||"📌")+" "+d.name).join(" · "), page:"planner", action:"Open Planner" });
    }
    if (niceOverdue.length > 0) {
      alerts.push({ id:"overdue_nice", severity:"info", icon:"📌",
        title:`${niceOverdue.length} nice-to-have decision${niceOverdue.length>1?"s":""} past date`,
        detail:niceOverdue.map(d=>(d.icon||"📌")+" "+d.name).join(" · "), page:"planner", action:"Open Planner" });
    }
  }

  const currentMonthKey = mkKey(today.getFullYear(), today.getMonth() + 1);
  const currentMonthData = months[currentMonthKey] || {};

  if (!currentMonthData.target || currentMonthData.target === 0) {
    alerts.push({
      id:"notarget", severity:"info", icon:"🎯",
      title:"No sales target set for this month",
      detail:"Without a target, cashflow projections will be inaccurate. Set your monthly unit target in Targets.",
      page:"targets", action:"Set Target",
    });
  }

  const hasPayoutConfigured = (marketplaces || []).some(m =>
    (m.products || []).some(p => (p.payout || 0) > 0)
  );
  if (!hasPayoutConfigured) {
    alerts.push({
      id:"nopayout", severity:"info", icon:"💰",
      title:"No payout/unit configured in any marketplace",
      detail:"Your income projections will show €0 until you set a payout per unit in Marketplaces.",
      page:"marketplaces", action:"Set Payout",
    });
  }

  const activeModifiers = (modifiers || []).filter(m => {
    if (!m.monthKeys?.length) return false;
    const todayKey2 = mkKey(today.getFullYear(), today.getMonth() + 1);
    return m.monthKeys.includes(todayKey2) || m.monthKeys.some(k => {
      const [y,mo] = k.split("-").map(Number);
      return new Date(y, mo-1, 1) >= today;
    });
  });

  if (activeModifiers.length > 0) {
    const unitsMods = activeModifiers.filter(m => m.type === "units");
    const incomeMods = activeModifiers.filter(m => m.type !== "units");

    if (unitsMods.length > 0) {
      const summary = unitsMods.map(m => {
        const pct = Math.round((m.value||0)*100);
        return `${m.name || "Unnamed"} (${pct>=0?"+":""}${pct}%)`;
      }).join(", ");
      alerts.push({
        id:"mod_units", severity:"warning", icon:"⚡",
        title:`${unitsMods.length} unit modifier${unitsMods.length>1?"s":""} active`,
        detail: summary + ". Your cashflow projections include adjusted unit volumes from these modifiers.",
        page:"adjustments", action:"View Modifiers",
      });
    }

    if (incomeMods.length > 0) {
      const summary = incomeMods.map(m => {
        const pct = Math.round((m.value||0)*100);
        return `${m.name || "Unnamed"} (${pct>=0?"+":""}${pct}%)`;
      }).join(", ");
      alerts.push({
        id:"mod_income", severity:"warning", icon:"💸",
        title:`${incomeMods.length} income modifier${incomeMods.length>1?"s":""} active`,
        detail: summary + ". Your cashflow income is adjusted by these modifiers.",
        page:"adjustments", action:"View Modifiers",
      });
    }
  }

  return alerts;
  } catch (err) {
    console.warn("[computeAlerts] caught:", err?.message || err);
    return [];
  }
}
